import {
  ConfigGroup,
  ConfigGroupValue,
  ConfigItem,
  ConfigItemType,
  ConfigItemValue,
  MultiSelectConfigItem,
  MultiSelectConfigValue,
  SelectConfigValue,
  SelectConfigValueOption,
} from '@ecosystem/newron-client-lib'
import { InsightData } from 'containers/charts'
import { Category, CategoryGroup, CategoryGroupName, Filter, Variable } from 'typings/filters'
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types'
import { RetailLostShopperTypes } from 'hooks/useSelectedVisitors'
import { AudienceSegmentThumbnail } from 'components/Segmentation/AudienceSegmentThumbnail'
import { staticSegmentDataMap } from 'hooks/useAudienceSegments/config'
import { Segment } from 'typings/segments'
import { SEGMENT_VARIABLE_ID } from 'hooks/useAudienceSegments'
import { Variables } from 'reducers/filtering/selectors'
import {
  brandEcosystemFilterGuardMsg,
  exceedFiltersLimitMessage,
  productAndCountryFilterGuardMsg,
  productGroupFilterGuardMsg,
  ProductGroupMarketFilterGuardMsg,
  reportingChannelVariables,
  unitedStatesFilterGuardMsg,
  US_ANSWER_ID,
  validContextGuardMsg,
} from 'components/FilterSummary'
import { FilterCombination, generateCombinationSet, hasMatch } from 'util/validateContext'
import { Cell } from 'reducers/subscription/user'
import { generalCategoryGroupLabelToName } from 'config/filters'

export interface GetConfigItemsArgs {
  data: InsightData
  dataType?: CollectionDataType
}

interface Answer {
  variable_id: number
  answer_id: number
  answer_code: number
  desc: string
  label: string
  categoryGroup: CategoryGroupName
  category_name?: string
}

interface CategoryFilters extends MultiSelectConfigItem {
  categoryGroup: CategoryGroupName
  isLoading: boolean
}

export type Option = SelectConfigValueOption & Answer & { leadingIcon?: JSX.Element }

export const constructAudienceSegmentThumbnail = (
  variableId: number,
  answerId: number,
  label: string
) => {
  let leadingIcon
  if (variableId === SEGMENT_VARIABLE_ID) {
    const { image, color } = staticSegmentDataMap[answerId as Segment]
    leadingIcon = (
      <AudienceSegmentThumbnail
        image={image}
        color={color}
        label={label}
        size={24}
        borderWidth={2}
      />
    )
  }
  return leadingIcon
}

export const getTimeFrameFilters = (data: InsightData) => {
  const timeFrameFilters =
    data?.graphqlVars?.filterNames?.filter(
      (f: Filter) =>
        f.variable_id === Variables.TIME_FRAME ||
        f.variable_id === Variables.TIME_FRAME_LIVE ||
        f.variable_id === Variables.TIME_FRAME_YEARLY
    ) ?? []
  return timeFrameFilters ?? []
}

export const constructFiltersItems = (
  appliedFilters: Filter[],
  subCategories: Variable[],
  groupName: string
): MultiSelectConfigItem[] => {
  const categoryGroup = generalCategoryGroupLabelToName?.[groupName]
  const subCategoryFilters = subCategories?.map((subCategory) => {
    const value: Option[] = appliedFilters
      ?.filter?.((filter) => filter.variable_id === subCategory.variable_id)
      ?.map((filter) => ({
        ...filter,
        leadingIcon: constructAudienceSegmentThumbnail(
          subCategory.variable_id,
          filter.answer_id,
          filter.label
        ),
        category_name: subCategory?.values?.find((v) => v.answer_code === filter.answer_code)
          ?.category_name,
        value: filter?.answer_code?.toString(),
      }))
    const categoryFilters: CategoryFilters = {
      key: subCategory.variable_id?.toString(),
      label: subCategory.label,
      type: ConfigItemType.MultiSelect,
      options: value,
      combobox: true,
      tooltipInfo: subCategory?.tooltip_content,
      value,
      placeholder: `Select a ${subCategory.label}`,
      showSelectAllOptions: false,
      disabled: false,
      categoryGroup,
      isLoading: true,
    }
    return categoryFilters
  })

  return subCategoryFilters
}

const constructSubGroup = (
  appliedFilters: Filter[],
  categories: Category[],
  groupName: string
): ConfigGroup[] => {
  const allCategoryFilters = categories
    ?.filter((category) => category.label !== 'Time Frame')
    ?.map((category) => {
      const group: ConfigGroup = {
        key: category.id?.toString(),
        label: category.label,
        type: ConfigItemType.Group,
        value: constructFiltersItems(appliedFilters, category.variables, groupName),
      }
      return group
    })
  return allCategoryFilters
}

export const getFilters = (
  appliedFilters: Filter[],
  allFilters: CategoryGroup[],
  context: Cell[]
): ConfigGroup[] => {
  const configItems: (ConfigGroup & { context: Cell[] })[] = allFilters?.map(
    (eachGroup, index) => ({
      key: eachGroup.id?.toString(),
      label: eachGroup.label,
      type: ConfigItemType.Group,
      value: constructSubGroup(appliedFilters, eachGroup.categories, eachGroup.label),
      context: index === 0 ? context : [],
    })
  )

  return configItems
}

export const getFilterNames = (configItems: ConfigItem[]): Filter[] => {
  let filterValues: Filter[] = []
  for (const eachGroup of configItems) {
    const subGroups: ConfigGroupValue = (eachGroup as ConfigGroup)?.value ?? []
    for (const eachSubGroup of subGroups ?? []) {
      for (const item of eachSubGroup?.value as ConfigGroupValue) {
        filterValues = [
          ...filterValues,
          ...(((item?.value ?? []) as Filter[])?.map((eachItem: Filter) => ({
            ...eachItem,
            variable_id: +item.key,
          })) ?? []),
        ]
      }
    }
  }
  return filterValues
}

export const getUpdatedFilters = <T extends ConfigItem>(
  filters: T[],
  newFilters: ConfigItemValue
): T[] => {
  const retailerLostShopperFilters = [
    RetailLostShopperTypes.TOTAL_RETAILER_LOST_SHOPPER,
    RetailLostShopperTypes.RETAILER_LOST_SHOPPER_INSTORE,
    RetailLostShopperTypes.RETAILER_LOST_SHOPPER_ONLINE,
  ]
  return filters.map((filter: T) => {
    let updatedFilter = { ...filter }
    if (filter?.type === ConfigItemType.MultiSelect && filter.key !== newFilters.key) {
      updatedFilter = { ...updatedFilter, isLoading: true }
    }

    // RETAILER LOST SHOPPER CONDITION
    if (
      retailerLostShopperFilters?.includes(+newFilters.key) &&
      retailerLostShopperFilters?.includes(+filter.key) &&
      updatedFilter.key !== newFilters?.key
    ) {
      if ((newFilters?.value as MultiSelectConfigValue)?.length) {
        return { ...updatedFilter, disabled: true }
      } else {
        return { ...updatedFilter, disabled: false }
      }
    }
    if (updatedFilter.key === newFilters.key) {
      const updatedValues = (updatedFilter as any)?.options?.filter(
        (option: Option) =>
          (newFilters?.value as ConfigGroupValue)?.findIndex(
            (nf) => option?.answer_code?.toString() === nf?.value
          ) > -1
      )
      return { ...updatedFilter, value: updatedValues, disabled: false }
    }
    return {
      ...updatedFilter,
      value: Array.isArray(updatedFilter.value)
        ? getUpdatedFilters(updatedFilter.value as ConfigGroupValue, newFilters)
        : updatedFilter.value,
    }
  })
}

export const getAllSelectedMultiSelectValues = (
  configItems: ConfigItem[]
): (SelectConfigValue & Answer)[] => {
  let selectedValues: (SelectConfigValue & Answer)[] = []
  for (const item of configItems) {
    if (item.type === ConfigItemType.MultiSelect && Array.isArray(item.value)) {
      selectedValues = [...selectedValues, ...item.value] as (SelectConfigValue & Answer)[]
    } else if (Array.isArray(item.value)) {
      selectedValues = [
        ...selectedValues,
        ...getAllSelectedMultiSelectValues(item.value as ConfigItem[]),
      ]
    }
  }
  return selectedValues
}

export const findConfigItemByKey = (
  configItems: ConfigItem[],
  key: string | number
): ConfigItem | null => {
  const keyStr = String(key)
  const search = (items: ConfigItem[] = []): ConfigItem | null => {
    for (const it of items) {
      if (
        String(it?.key) === keyStr &&
        (it.type === ConfigItemType.MultiSelect || it.type === ConfigItemType.Select)
      ) {
        return it
      }
      if (Array.isArray(it?.value)) {
        const found = search(it.value as ConfigItem[])
        if (found) return found
      }
    }
    return null
  }
  return search(configItems)
}

export const getLabelAndCategoryGroupByKey = (
  configItems: ConfigItem[],
  key: string | number
): { label?: string; categoryGroup?: string } => {
  const item = findConfigItemByKey(configItems, key)
  const label = (item as ConfigGroup)?.label
  const categoryGroup = item?.categoryGroup
  return { label, categoryGroup }
}

const productCountrySelection = (selectedFilters: (SelectConfigValue & Answer)[]) => {
  return (
    !selectedFilters?.find((filter) => filter?.variable_id === Variables.PRODUCT_GROUP) ||
    !selectedFilters?.find((filter) => filter?.variable_id === Variables.COUNTRY)
  )
}
const exceedsTotalSelectionCount = (selectedFilters: (SelectConfigValue & Answer)[]) => {
  return selectedFilters?.length > 60
}

const isUSSelectedCriteria = (selectedFilters: (SelectConfigValue & Answer)[]) =>
  selectedFilters.filter((filter) => filter.answer_id === US_ANSWER_ID).length === 1 &&
  selectedFilters.filter((filter) => filter.variable_id === Variables.COUNTRY).length > 1

const productSelectedFilters = (selectedFilters: (SelectConfigValue & Answer)[]) =>
  selectedFilters.filter((filter) => filter.variable_id === Variables.PRODUCT_GROUP)

const countrySelectedFilters = (selectedFilters: (SelectConfigValue & Answer)[]) =>
  selectedFilters.filter((filter) => filter.variable_id === Variables.COUNTRY)

const isEcosystemFilterValid = (selectedFilters: (SelectConfigValue & Answer)[]) => {
  const brandEcosystemFilter = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.BRAND_ECOSYSTEM
  )
  const brandPurchasedFilter = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.BRAND_PURCHASED
  )

  return (
    brandEcosystemFilter.length > 0 &&
    !(brandPurchasedFilter.length === 1 && productSelectedFilters(selectedFilters)?.length === 1)
  )
}

const isProductFeatureImportanceFilterValid = (selectedFilters: (SelectConfigValue & Answer)[]) => {
  const productFeatureImportanceFilter = selectedFilters.filter(
    (filter) => filter.variable_id === Variables.FEATURE_IMPORTANCE
  )

  return (
    productFeatureImportanceFilter.length > 0 &&
    productSelectedFilters(selectedFilters)?.length !== 1
  )
}

const isReportingChannelFiltersApplied = (selectedFilters: (SelectConfigValue & Answer)[]) => {
  const reportingChannelFilters = selectedFilters.filter((filter) =>
    reportingChannelVariables?.includes(filter.variable_id)
  )
  return (
    reportingChannelFilters.length > 0 &&
    (productSelectedFilters(selectedFilters)?.length !== 1 ||
      countrySelectedFilters(selectedFilters)?.length !== 1)
  )
}

const isValidContext = (selectedFilters: (SelectConfigValue & Answer)[], context: Cell[]) => {
  const productFilters: FilterCombination[] = productSelectedFilters(selectedFilters)?.map((f) => ({
    ...f,
    categoryName: f.category_name,
  }))
  const marketFilters: FilterCombination[] = countrySelectedFilters(selectedFilters)?.map((f) => ({
    ...f,
    categoryName: f.category_name,
  }))
  const combinationSet = generateCombinationSet(productFilters, marketFilters)
  const isValid = hasMatch(combinationSet, context) ?? true
  return isValid
}

export const checkForErrors = (configItems: (ConfigGroup & { context: Cell[] })[]) => {
  let errorMessage = ''
  const selectedItems = getAllSelectedMultiSelectValues(configItems)

  const errorScenarios = [
    {
      condition: productCountrySelection(selectedItems),
      message: ProductGroupMarketFilterGuardMsg,
    },
    {
      condition: exceedsTotalSelectionCount(selectedItems),
      message: exceedFiltersLimitMessage,
    },
    {
      condition: isUSSelectedCriteria(selectedItems),
      message: unitedStatesFilterGuardMsg,
    },
    {
      condition: isEcosystemFilterValid(selectedItems),
      message: brandEcosystemFilterGuardMsg,
    },
    {
      condition: !isValidContext(selectedItems, configItems?.[0]?.context),
      message: validContextGuardMsg,
    },
    {
      condition: isProductFeatureImportanceFilterValid(selectedItems),
      message: productGroupFilterGuardMsg,
    },
    {
      condition: isReportingChannelFiltersApplied(selectedItems),
      message: productAndCountryFilterGuardMsg,
    },
  ]

  for (const scenario of errorScenarios) {
    if (scenario.condition) {
      errorMessage = `${scenario.message.errorMsg} (${scenario.message.errorDetail})`
      break
    }
  }

  return errorMessage
}
